import { usePostHog } from 'posthog-js/react';
import { useAuth } from '../context/AuthContext';
import {
  currentProductEventEmail,
  withProductEmail,
} from './productEventEmail';

/**
 * Critical Analytics for Memara - Quota-Conscious Event Tracking
 * Only tracks the most essential business metrics to preserve PostHog free tier
 * 
 * Priority Events:
 * 1. User Acquisition (signups, verification)
 * 2. Activation (first memory created)
 * 3. Retention (weekly/monthly memory usage)
 * 4. Revenue (subscription events)
 * 5. Product-Market Fit (key feature adoption)
 */

// Environment-based sampling rate
const SAMPLING_RATE = import.meta.env.VITE_ANALYTICS_SAMPLING_RATE 
  ? parseFloat(import.meta.env.VITE_ANALYTICS_SAMPLING_RATE)
  : 0.1; // 10% sampling by default

// Helper function to determine if event should be tracked (sampling)
const shouldTrackEvent = (alwaysTrack: boolean = false): boolean => {
  if (alwaysTrack) return true;
  return Math.random() < SAMPLING_RATE;
};

// Throttle high-volume events (max once per time period)
const eventThrottleMap = new Map<string, number>();
const throttleEvent = (eventKey: string, throttleMs: number): boolean => {
  const now = Date.now();
  const lastTracked = eventThrottleMap.get(eventKey);
  
  if (!lastTracked || now - lastTracked > throttleMs) {
    eventThrottleMap.set(eventKey, now);
    return true;
  }
  return false;
};

// Helper function to calculate days since last signin
const calculateDaysSinceLastSignin = (): number => {
  const lastSignin = localStorage.getItem('last_signin_timestamp');
  if (!lastSignin) return 0;
  
  const lastSigninDate = new Date(lastSignin);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - lastSigninDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

export const useCriticalAnalytics = () => {
  const posthog = usePostHog();
  const { user } = useAuth();
  const knownEmail = user?.email;
  const captureProduct = (
    event: string,
    properties: Record<string, unknown>,
    email?: string | null,
  ) => {
    posthog?.capture(event, withProductEmail(properties, email || knownEmail));
  };

  return {
    // 🎯 PRIORITY 1: User Acquisition & Activation
    trackUserSignup: (method: 'email' | 'google', email?: string) => {
      captureProduct(
        'user_signed_up',
        {
          method,
          signup_timestamp: new Date().toISOString(),
          referrer: document.referrer || 'direct',
        },
        email,
      );
    },

    trackUserSignin: (method: 'email' | 'google', isReturningUser: boolean) => {
      captureProduct('user_signed_in', {
        method,
        is_returning_user: isReturningUser,
        signin_timestamp: new Date().toISOString(),
        days_since_last_signin: isReturningUser ? calculateDaysSinceLastSignin() : 0,
      });
    },

    trackEmailVerification: (email: string) => {
      captureProduct(
        'email_verified',
        {
          verification_timestamp: new Date().toISOString(),
        },
        email,
      );
    },

    trackFirstMemoryCreated: (data: {
      content_length: number;
      time_to_first_memory_hours: number;
      tags_count: number;
    }) => {
      captureProduct('first_memory_created', {
        ...data,
        milestone: 'activation_achieved',
        timestamp: new Date().toISOString(),
      });
    },

    // 🎯 PRIORITY 2: Revenue Events (Always Track)
    trackSubscriptionStarted: (plan: string, revenue: number, billing_cycle: string) => {
      captureProduct('subscription_started', {
        plan,
        revenue,
        billing_cycle,
        timestamp: new Date().toISOString(),
      });
    },

    trackSubscriptionUpgraded: (from_plan: string, to_plan: string, revenue: number) => {
      captureProduct('subscription_upgraded', {
        from_plan,
        to_plan,
        revenue,
        timestamp: new Date().toISOString(),
      });
    },

    trackSubscriptionCancelled: (plan: string, reason?: string) => {
      captureProduct('subscription_cancelled', {
        plan,
        cancellation_reason: reason || 'user_action',
        timestamp: new Date().toISOString(),
      });
    },

    // 🎯 PRIORITY 3: Key Product Adoption (Sampled)
    trackMcpIntegrationUsed: (integration_type: 'claude' | 'cursor' | 'openai' | 'custom') => {
      if (shouldTrackEvent()) {
        captureProduct('mcp_integration_used', {
          integration_type,
          milestone: 'integration_adoption',
          timestamp: new Date().toISOString(),
        });
      }
    },

    trackMemorySearchFirstTime: () => {
      captureProduct('memory_search_first_time', {
        milestone: 'search_feature_adopted',
        timestamp: new Date().toISOString(),
      });
    },

    trackMemorySpaceCreated: (space_name: string, is_first_space: boolean) => {
      if (shouldTrackEvent(is_first_space)) { // Always track first space
        captureProduct('memory_space_created', {
          space_name,
          is_first_space,
          timestamp: new Date().toISOString(),
        });
      }
    },

    trackIntegrationConnected: (
      integration_type: string,
      is_first_integration: boolean = false,
      setup_time_ms?: number
    ) => {
      if (shouldTrackEvent(is_first_integration)) { // Always track first integration
        captureProduct('integration_connected', {
          integration_type,
          is_first_integration,
          setup_time_ms,
          milestone: 'integration_adoption',
          timestamp: new Date().toISOString(),
        });
      }
    },

    // 🎯 PRIORITY 4: Retention Indicators (Throttled)
    trackWeeklyMemoryUsage: (memories_created: number, searches_performed: number) => {
      const eventKey = `weekly_usage_${Math.floor(Date.now() / (7 * 24 * 60 * 60 * 1000))}`;
      
      if (throttleEvent(eventKey, 6 * 24 * 60 * 60 * 1000)) { // Once per 6 days max
        captureProduct('weekly_memory_usage', {
          memories_created,
          searches_performed,
          usage_level: memories_created >= 10 ? 'high' : memories_created >= 3 ? 'medium' : 'low',
          timestamp: new Date().toISOString(),
        });
      }
    },

    trackMonthlyRetention: (days_active: number, total_memories: number) => {
      const eventKey = `monthly_retention_${Math.floor(Date.now() / (30 * 24 * 60 * 60 * 1000))}`;
      
      if (throttleEvent(eventKey, 25 * 24 * 60 * 60 * 1000)) { // Once per 25 days max
        captureProduct('monthly_retention', {
          days_active,
          total_memories,
          retention_level: days_active >= 20 ? 'high' : days_active >= 10 ? 'medium' : 'low',
          timestamp: new Date().toISOString(),
        });
      }
    },

    // 🎯 PRIORITY 5: Critical User Journey (Always Track)
    trackOnboardingCompleted: (completion_time_minutes: number) => {
      captureProduct('onboarding_completed', {
        completion_time_minutes,
        milestone: 'onboarding_success',
        timestamp: new Date().toISOString(),
      });
    },

    trackRealmFirstEntry: (realm: 'sanctuary') => {
      captureProduct('realm_first_entry', {
        realm,
        milestone: 'realm_discovery',
        timestamp: new Date().toISOString(),
      });
    },

    // 🎯 PRIORITY 6: Business Intelligence (Sampled)
    trackFeatureDiscovery: (feature: string, discovery_method: string) => {
      if (shouldTrackEvent()) {
        captureProduct('feature_discovered', {
          feature,
          discovery_method,
          timestamp: new Date().toISOString(),
        });
      }
    },

    trackUserFeedback: (rating: number, feedback: string, context: string) => {
      captureProduct('user_feedback', {
        rating,
        feedback_length: feedback.length,
        context,
        sentiment: rating >= 4 ? 'positive' : rating >= 3 ? 'neutral' : 'negative',
        timestamp: new Date().toISOString(),
      });
    },

    // 🎯 CRITICAL ERROR TRACKING ONLY
    trackCriticalError: (error_type: string, error_message: string, context?: string) => {
      posthog?.capture('critical_error', {
        error_type,
        error_message,
        context,
        timestamp: new Date().toISOString(),
      });
    },

    // 🎯 USER PROPERTIES (Efficient Updates)
    updateUserMilestone: (milestone: string, data?: Record<string, any>) => {
      posthog?.setPersonProperties({
        [`milestone_${milestone}`]: new Date().toISOString(),
        ...data,
      });
    },

    updateUserTier: (tier: string, previous_tier?: string) => {
      posthog?.setPersonProperties({
        tier,
        previous_tier,
        tier_updated_at: new Date().toISOString(),
      });
    },
  };
};

/**
 * Quick conversion tracking for key business events
 * These should be called at the most critical moments
 */
export const trackCriticalConversion = (
  event: 'signup' | 'email_verified' | 'first_memory' | 'subscription_started' | 'mcp_connected'
) => {
  if (typeof window !== 'undefined' && (window as any).posthog) {
    (window as any).posthog.capture(`conversion_${event}`, {
      ...currentProductEventEmail(),
      conversion_type: event,
      timestamp: new Date().toISOString(),
    });
  }
};

/**
 * Key Business Metrics Summary
 * These are the events that matter most for Memara's business success:
 * 
 * 1. User Acquisition: user_signed_up, email_verified
 * 2. Activation: first_memory_created, onboarding_completed
 * 3. Feature Adoption: mcp_integration_used, memory_search_first_time
 * 4. Retention: weekly_memory_usage, monthly_retention
 * 5. Revenue: subscription_started, subscription_upgraded, subscription_cancelled
 * 6. Product-Market Fit: user_feedback, feature_discovered
 */

export const CRITICAL_BUSINESS_EVENTS = [
  'user_signed_up',
  'email_verified', 
  'first_memory_created',
  'subscription_started',
  'subscription_upgraded',
  'subscription_cancelled',
  'mcp_integration_used',
  'integration_connected',
  'weekly_memory_usage',
  'monthly_retention',
  'user_feedback',
  'critical_error'
] as const; 